import { Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
import { ArrowRight, Leaf, Users, ShieldCheck, Recycle, HeartHandshake, Building2, type LucideIcon } from "lucide-react";
import { type Locale } from "@/i18n/config";
import { pathFor } from "@/i18n/slugMap";
import { pageHead } from "@/i18n/head";
import { esgStrings } from "@/i18n/content/esg";
import { Img, SIZES } from "@/components/Img";

/** <head> for the ESG page. */
export function esgHead(locale: Locale) {
  const s = esgStrings[locale];
  return pageHead("esg", locale, s.meta, { breadcrumbName: s.hero.title });
}

// Icons aligned by index with the catalog card arrays.
const pillarIcons: LucideIcon[] = [Leaf, Users, ShieldCheck];
const initiativeIcons: LucideIcon[] = [Recycle, Building2, HeartHandshake, ShieldCheck];

export function EsgPage({ locale }: { locale: Locale }) {
  const t = esgStrings[locale];

  return (
    <SiteLayout>
      {/* Hero */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <span className="inline-block rounded-full bg-secondary px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-foreground/85">
            {t.hero.badge}
          </span>
          <h1 className="mt-6 text-4xl md:text-6xl font-bold tracking-tight">{t.hero.title}</h1>
          <p className="mt-6 text-lg text-foreground/85 leading-relaxed">{t.hero.p1}</p>
          <p className="mt-4 text-lg text-foreground/85 leading-relaxed">
            <span className="font-semibold text-primary">{t.hero.iso}</span>
            {t.hero.pAfter}
          </p>
        </div>
      </section>

      {/* ISO 14001 highlight */}
      <section className="bg-section-light text-section-light-foreground py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid gap-12 lg:grid-cols-2 items-center">
          <Img
            src="/wp-content/uploads/Kontor-gruppe-4_1400x1400.jpg"
            alt={t.iso.heading}
            className="rounded-2xl w-full aspect-square object-cover"
            sizes={SIZES.split}
          />
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary">
              <Leaf className="h-3.5 w-3.5" /> {t.iso.badge}
            </span>
            <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight">{t.iso.heading}</h2>
            <p className="mt-6 leading-relaxed">{t.iso.p1}</p>
            <p className="mt-4 leading-relaxed">{t.iso.p2}</p>
            <p className="mt-4 leading-relaxed">{t.iso.p3}</p>
            <Link to={pathFor("kontakt", locale)} className="mt-8 btn-cta">
              {t.iso.cta} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ESG pillars */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.pillars.heading}</h2>
            <p className="mt-4 text-foreground/85 leading-relaxed">{t.pillars.intro}</p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {t.pillars.cards.map((p, i) => {
              const Icon = pillarIcons[i] ?? Leaf;
              return (
                <div key={p.title} className="rounded-2xl bg-secondary p-8">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/15 text-primary">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 text-xl font-bold">{p.title}</h3>
                  <p className="mt-3 text-foreground/90 leading-relaxed">{p.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Initiatives */}
      <section className="bg-secondary py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.initiatives.heading}</h2>
            <p className="mt-4 text-foreground/90 leading-relaxed">{t.initiatives.intro}</p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {t.initiatives.cards.map((c, i) => {
              const Icon = initiativeIcons[i] ?? Recycle;
              return (
                <div key={c.title} className="rounded-2xl bg-background p-8">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/15 text-primary">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 text-xl font-bold">{c.title}</h3>
                  <p className="mt-3 text-foreground/90 leading-relaxed">{c.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SDGs */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="max-w-3xl">
            <span className="inline-block rounded-full bg-primary/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary">
              {t.sdgsSection.badge}
            </span>
            <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight">{t.sdgsSection.heading}</h2>
            <p className="mt-4 text-foreground/85 leading-relaxed">{t.sdgsSection.intro}</p>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {t.sdgs.map((s) => (
              <div key={s.num} className="rounded-2xl bg-secondary p-6 flex flex-col">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-lg">
                  {s.num}
                </div>
                <h3 className="mt-4 font-bold">{s.title}</h3>
                <p className="mt-2 text-sm text-foreground/85 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-section-light text-section-light-foreground py-20">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.cta.heading}</h2>
          <p className="mt-6 leading-relaxed">{t.cta.body}</p>
          <Link to={pathFor("kontakt", locale)} className="mt-8 btn-cta">
            {t.cta.cta} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}
